import { useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import HTMLParse from 'react-html-parser'
import Meta from '@/components/Meta'
import HeroHome from '@/components/HeroHome'
import ScrollingStroke from '@/components/ScrollingStroke'
import PageElements from '@/components/PageElements'
import Footer from '@/components/Footer'
import usePagePrecache from '@/hooks/usePagePrecache'
import useResizeObserver from '@/hooks/useResizeObserver'
import * as contentActions from '@/actions/content'
import style from './style'

const useStyles = createUseStyles(style)

const HomePage = ({
  stub: stubFromProps,
}) => {
  const $root = useRef()
  const classes = useStyles()
  const stub = useRef(stubFromProps)
  const [isDataFetched, setDataFetched] = useState(false)

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { page, isPageAnimationReady } = useSelector((state) => ({
    page: state.content.pages[stub.current] || {},
    isPageAnimationReady: state.loading.isPageAnimationReady,
    isLoaderExited: state.loading.isLoaderExited,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const fetchPage = useCallback((slug) => dispatch(contentActions.fetchPage(slug)), [dispatch])

  /*------------------------------
  Did Mount (Fetch Data)
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length === 0) fetchPage(stub.current)
  }, [])

  /*------------------------------
  Check data Fetched
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) setDataFetched(true)
  }, [page])

  /*------------------------------
  Preload
  ------------------------------*/
  const [load] = usePagePrecache({
    init: isDataFetched,
    sources: [],
    callback: () => {},
  })

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => load && <Meta meta={page.yoast_head} />

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = useCallback(() => {
    let content
    if (page.content.rendered) {
      content = HTMLParse(page.content.rendered)
    }
    return content && (
      <HeroHome
        word1={content[0].props.children[0]}
        word2={content[1].props.children[0]}
        word3={content[2].props.children[0]}
        visible={isPageAnimationReady}
      />
    )
  }, [page, isPageAnimationReady])

  /*------------------------------
  Render page builder
  ------------------------------*/
  const renderPageElements = useCallback(() => {
    return page.acf.page_builder && (
      <PageElements elements={page.acf.page_builder} />
    )
  }, [page])

  useResizeObserver({
    ref: $root.current,
    callback: () => {
      window.dispatchEvent(new Event('resize'))
    },
  })

  return (
    <>
      {renderHelmet()}
      {load && (
        <div className={classes.root} ref={$root}>
          <div className={classes.page}>
            <ScrollingStroke />
            <div className={classes.wrapper}>
              {renderHero()}
              {renderPageElements()}
              <Footer className={classes.footer} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HomePage
